/**
 * Storyblok API optimization configuration
 * Centralized settings for rate limiting, caching, and batching
 */

export const STORYBLOK_OPTIMIZATION_CONFIG = {
  // Rate limiting configuration
  RATE_LIMITING: {
    // Maximum requests per second during build
    MAX_REQUESTS_PER_SECOND: 2,
    
    // Maximum concurrent requests
    MAX_CONCURRENT_REQUESTS: 3,
    
    // Retry configuration
    MAX_RETRIES: 8,
    INITIAL_DELAY: 1000, // ms
    MAX_DELAY: 5000, // ms
    BACKOFF_FACTOR: 1.5,
  },

  // Caching configuration
  CACHING: {
    // Build-time cache TTL (5 minutes)
    BUILD_CACHE_TTL: 300000,
    
    // Production cache revalidation time (3 hours)
    PRODUCTION_REVALIDATE: 10800,
    
    // Enable aggressive caching during build
    ENABLE_BUILD_CACHING: true,
  },

  // Batching configuration
  BATCHING: {
    // Delay to wait for collecting similar requests
    BATCH_DELAY: 100, // ms
    
    // Maximum batch size for concurrent processing
    MAX_BATCH_SIZE: 5,
    
    // Delay between processing different batches
    BATCH_PROCESSING_DELAY: 100, // ms
  },

  // Sitemap generation optimization
  SITEMAP: {
    // Delay between processing different locales
    LOCALE_PROCESSING_DELAY: 300, // ms
    
    // Delay between processing different content types
    CONTENT_TYPE_DELAY: 200, // ms
    
    // Maximum stories per page for sitemap generation
    STORIES_PER_PAGE: 100,
  },

  // Static params generation optimization
  STATIC_PARAMS: {
    // Delay between different static param generators
    GENERATOR_DELAY: 200, // ms
    
    // Delay between processing different locales
    LOCALE_DELAY: 150, // ms
    
    // Enable sequential processing instead of concurrent
    ENABLE_SEQUENTIAL_PROCESSING: true,
  },

  // Development/debugging settings
  DEBUG: {
    // Enable verbose logging
    ENABLE_LOGGING: process.env.NODE_ENV === 'development',
    
    // Log cache hits and misses
    LOG_CACHE_STATS: false,
    
    // Log rate limiting events
    LOG_RATE_LIMITING: true,
  },
  // Environment-specific overrides
  ENVIRONMENT_OVERRIDES: {
    // More aggressive settings for production builds
    production: {
      RATE_LIMITING: {
        MAX_REQUESTS_PER_SECOND: 1.5, // Even more conservative for builds
        MAX_CONCURRENT_REQUESTS: 2,
      },
      CACHING: {
        ENABLE_BUILD_CACHING: true,
        CACHE_TTL: 300000, // 5 minutes
        MAX_CACHE_SIZE: 1000,
      },
      BATCHING: {
        BATCH_DELAY: 200, // Longer delays in production
        BATCH_PROCESSING_DELAY: 150,
      },
    },
    
    // Faster settings for development
    development: {
      RATE_LIMITING: {
        MAX_REQUESTS_PER_SECOND: 3,
        MAX_CONCURRENT_REQUESTS: 4,
      },
      CACHING: {
        ENABLE_BUILD_CACHING: false, // Disable caching in dev for fresh data
        CACHE_TTL: 60000, // 1 minute
        MAX_CACHE_SIZE: 500,
      },
      BATCHING: {
        BATCH_DELAY: 100,
        BATCH_PROCESSING_DELAY: 50,
      },
    },
  },
};

/**
 * Get configuration with environment-specific overrides applied
 */
export function getOptimizationConfig() {
  const baseConfig = { ...STORYBLOK_OPTIMIZATION_CONFIG };
  const env = process.env.NODE_ENV || 'development';
  
  if (env in baseConfig.ENVIRONMENT_OVERRIDES) {
    const overrides = baseConfig.ENVIRONMENT_OVERRIDES[env as keyof typeof baseConfig.ENVIRONMENT_OVERRIDES];
    
    // Deep merge overrides with safe property access
    return {
      ...baseConfig,
      RATE_LIMITING: { ...baseConfig.RATE_LIMITING, ...(overrides.RATE_LIMITING || {}) },
      CACHING: { ...baseConfig.CACHING, ...(overrides.CACHING || {}) },
      BATCHING: { ...baseConfig.BATCHING, ...(overrides.BATCHING || {}) },
    };
  }
  
  return baseConfig;
}

export default STORYBLOK_OPTIMIZATION_CONFIG;
